// NotificationsHost — top-of-screen toast stack driven by the `notifications`
// slot + the host's event bus.
//
// Authoring model: operators place `notification` blocks in the `notifications`
// slot. Each declares a `trigger` (a NotificationTrigger), a message (literal
// `text` or a server-resolved DVar in `text`), and a `timeout` (ms; 0 = stays
// until dismissed). When the app fires a trigger via host.notify(), every
// notification block with that trigger surfaces as a toast at the top of the
// screen — on both desktop and mobile.
//
// Behaviour (per spec):
//   - Toasts stack vertically, newest at the top.
//   - Re-firing a trigger whose toast is already visible refreshes its timer
//     and re-animates it rather than adding a duplicate.
//   - timeout > 0 auto-dismisses after that many ms; timeout === 0 persists
//     until the user dismisses it with the ×.
//
// The stack renders through a portal to <body> so it floats above the whole
// configurator (sheet, modals, canvas) regardless of where it's mounted.

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { BlockInstance, NotificationTrigger } from "../blocks/types";

/** One live toast on screen. `key` dedupes by source block so a repeat trigger
 *  refreshes instead of stacking. `nonce` bumps on refresh to re-trigger the
 *  enter animation. */
interface ActiveToast {
  key: string;
  message: string;
  timeout: number;
  nonce: number;
}

const DEFAULT_TIMEOUT_MS = 4000;

/**
 * Imperative controller returned by useNotifications. `notify` is handed to the
 * block host context; the rest drive <NotificationsHost>.
 */
export interface NotificationsController {
  notify: (trigger: NotificationTrigger) => void;
  toasts: ActiveToast[];
  dismiss: (key: string) => void;
}

/**
 * Owns toast state + the notify entry point. Pass it the resolved `notifications`
 * slot entries so notify() can resolve a trigger → message/timeout. Resolution
 * happens at fire time so live mapping edits (focus previews) are picked up.
 */
export function useNotifications(entries: BlockInstance[] | undefined): NotificationsController {
  const [toasts, setToasts] = useState<ActiveToast[]>([]);
  const entriesRef = useRef<BlockInstance[] | undefined>(entries);
  entriesRef.current = entries;
  // Per-toast auto-dismiss timers, keyed by toast key.
  const timersRef = useRef<Map<string, number>>(new Map());

  const clearTimer = useCallback((key: string) => {
    const t = timersRef.current.get(key);
    if (t != null) {
      window.clearTimeout(t);
      timersRef.current.delete(key);
    }
  }, []);

  const dismiss = useCallback((key: string) => {
    clearTimer(key);
    setToasts(prev => prev.filter(t => t.key !== key));
  }, [clearTimer]);

  const armTimer = useCallback((key: string, timeout: number) => {
    clearTimer(key);
    if (timeout > 0) {
      timersRef.current.set(key, window.setTimeout(() => dismiss(key), timeout));
    }
  }, [clearTimer, dismiss]);

  const notify = useCallback((trigger: NotificationTrigger) => {
    const list = entriesRef.current ?? [];
    for (const b of list) {
      if (b.block !== "notification") continue;
      const p = (b.props ?? {}) as Record<string, unknown>;
      if (p.trigger !== trigger) continue;
      const message = typeof p.text === "string" ? p.text : "";
      if (!message) continue;
      const timeout = typeof p.timeout === "number" ? p.timeout : DEFAULT_TIMEOUT_MS;
      const key = b.key; // stable per block → dedupe target

      setToasts(prev => {
        const existing = prev.find(t => t.key === key);
        if (existing) {
          // Refresh: bump nonce (re-animate) + keep at the top.
          const refreshed: ActiveToast = { ...existing, message, timeout, nonce: existing.nonce + 1 };
          return [refreshed, ...prev.filter(t => t.key !== key)];
        }
        return [{ key, message, timeout, nonce: 0 }, ...prev];
      });
      armTimer(key, timeout);
    }
  }, [armTimer]);

  // Clean up any outstanding timers on unmount.
  useEffect(() => () => {
    timersRef.current.forEach(t => window.clearTimeout(t));
    timersRef.current.clear();
  }, []);

  return { notify, toasts, dismiss };
}

export interface NotificationsHostProps {
  toasts: ActiveToast[];
  onDismiss: (key: string) => void;
}

export function NotificationsHost({ toasts, onDismiss }: NotificationsHostProps) {
  if (typeof document === "undefined") return null;
  if (toasts.length === 0) return null;
  return createPortal(
    <div className="sd-toasts" role="region" aria-label="Notifications">
      {toasts.map(t => (
        // Key includes the nonce so a refresh remounts the node and replays the
        // enter animation (CSS animations don't restart on attribute change).
        <div key={t.key + ":" + t.nonce} className="sd-toast" role="status" aria-live="polite">
          <span className="sd-toast__message">{t.message}</span>
          <button
            type="button"
            className="sd-toast__close"
            onClick={() => onDismiss(t.key)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      ))}
    </div>,
    document.body
  );
}
